import { Controller } from "@hotwired/stimulus"

// Shows the preview card for a calendar entry in one shared floating panel.
//
// Each chip carries its card inside a <template>, so opening a popover costs no request —
// the data is already on the page. The panel is positioned next to the clicked chip and
// flipped when it would leave the viewport.
const GAP = 8
const MARGIN = 8

export default class extends Controller {
  static targets = ["panel"]

  connect() {
    this.onDocumentClick = this.handleDocumentClick.bind(this)
    this.onKeydown = this.handleKeydown.bind(this)
    this.onReposition = this.hide.bind(this)

    document.addEventListener("click", this.onDocumentClick)
    document.addEventListener("keydown", this.onKeydown)
    window.addEventListener("resize", this.onReposition)
    // Any scroll under the panel would leave it floating next to nothing.
    window.addEventListener("scroll", this.onReposition, true)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    document.removeEventListener("keydown", this.onKeydown)
    window.removeEventListener("resize", this.onReposition)
    window.removeEventListener("scroll", this.onReposition, true)
  }

  show(event) {
    const trigger = event.currentTarget
    const template = trigger.querySelector("template")
    if (!template || !this.hasPanelTarget) return

    event.preventDefault()
    event.stopPropagation()

    // Clicking the chip that is already open closes it again.
    if (this.activeTrigger === trigger && !this.panelTarget.classList.contains("hidden")) {
      this.hide()
      return
    }

    this.activeTrigger = trigger
    this.panelTarget.replaceChildren(template.content.cloneNode(true))
    this.panelTarget.classList.remove("hidden")
    this.position(trigger)
  }

  hide() {
    if (!this.hasPanelTarget) return
    this.panelTarget.classList.add("hidden")
    this.panelTarget.replaceChildren()
    this.activeTrigger = null
  }

  position(trigger) {
    const panel = this.panelTarget
    const anchor = trigger.getBoundingClientRect()
    const { width, height } = panel.getBoundingClientRect()

    // Prefer the right of the chip, fall back to its left, then clamp into the viewport.
    let left = anchor.right + GAP
    if (left + width > window.innerWidth - MARGIN) left = anchor.left - width - GAP
    left = Math.min(Math.max(left, MARGIN), window.innerWidth - width - MARGIN)

    let top = anchor.top
    if (top + height > window.innerHeight - MARGIN) top = window.innerHeight - height - MARGIN
    top = Math.max(top, MARGIN)

    panel.style.left = `${left}px`
    panel.style.top = `${top}px`
  }

  handleDocumentClick(event) {
    if (!this.hasPanelTarget) return
    if (this.panelTarget.classList.contains("hidden")) return
    if (this.panelTarget.contains(event.target)) return
    if (this.activeTrigger && this.activeTrigger.contains(event.target)) return

    this.hide()
  }

  handleKeydown(event) {
    if (event.key !== "Escape") return
    if (this.hasPanelTarget && !this.panelTarget.classList.contains("hidden")) {
      event.stopPropagation()
      this.hide()
    }
  }
}
